import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Clock, Music, ArrowRight, Star, BadgeCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { HeroSection, StatsSection, HowItWorksSection, CtaSection } from '../components/landing';
import { ArtistImage } from '../components/ArtistImage';

export default function Landing() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, user, isAdmin } = useAuth();
  
  // Check if user is an artist (artists can't see prices or book other artists)
  const isArtist = user?.role === 'artist';
  const canSeePrices = isAuthenticated && !isArtist;
  
  // Admin should be redirected to admin panel for artist management
  const browseArtistsLink = isAdmin ? "/admin?tab=artists" : "/artists";

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await api.getArtists();
        setArtists(response.data);
      } catch (error) {
        toast.error('Failed to fetch artists');
      } finally {
        setLoading(false);
      }
    };
    fetchArtists();
  }, []);

  // Only show first 5 artists as featured
  const featuredArtists = artists.slice(0, 5);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      {/* Hero Section */}
      <HeroSection
        browseArtistsLink={browseArtistsLink}
        isAdmin={isAdmin}
        isAuthenticated={isAuthenticated}
      />

      {/* Stats Section */}
      <StatsSection artistCount={artists.length} />

      {/* Featured Artists Section */}
      <section id="artists" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12"
          >
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Featured Artists</h2>
              <p className="text-[#A1A1AA] max-w-xl">
                Handpicked top talents in Dancehall and Reggae. Each dubplate is recorded exclusively for you.
              </p>
            </div>
            <Link 
              to={browseArtistsLink} 
              className="btn-secondary inline-flex items-center gap-2 whitespace-nowrap"
              data-testid="view-all-artists"
            >
              View All Artists
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="artist-card animate-pulse">
                  <div className="aspect-[4/3] bg-surface-highlight" />
                  <div className="p-6">
                    <div className="h-6 bg-surface-highlight rounded mb-3 w-3/4" />
                    <div className="h-4 bg-surface-highlight rounded mb-4 w-full" />
                    <div className="h-8 bg-surface-highlight rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : featuredArtists.length === 0 ? (
            <div className="text-center py-20">
              <Music className="w-16 h-16 mx-auto mb-4 text-[#333]" />
              <h3 className="text-xl font-heading font-bold mb-2">No Artists Yet</h3>
              <p className="text-[#A1A1AA]">Artists will be added soon. Check back later!</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {featuredArtists.map((artist, index) => (
                <motion.div key={artist.id} variants={itemVariants}>
                  <Link
                    to={`/artist/${artist.id}`}
                    className="artist-card block group"
                    data-testid={`featured-artist-${index}`}
                  >
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <ArtistImage
                        src={artist.image_url}
                        alt={artist.name}
                        name={artist.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        {canSeePrices ? (
                          <span className="price-tag">${artist.price.toFixed(2)}</span>
                        ) : (
                          <span className="price-tag flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            {isArtist ? 'Artist account' : 'Login to see price'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold mb-2 group-hover:text-lime transition-colors flex items-center gap-2">
                        {artist.name}
                        {artist.is_verified && (
                          <BadgeCheck className="w-5 h-5 text-blue-400" />
                        )}
                      </h3>
                      <p className="text-sm text-[#A1A1AA] mb-4 line-clamp-2">{artist.bio}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-[#666]">
                          <Clock className="w-4 h-4" />
                          <span>{artist.delivery_days} days delivery</span>
                        </div>
                        {artist.average_rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-lime fill-lime" />
                            <span className="text-sm text-lime">{artist.average_rating}</span>
                          </div>
                        )}
                        <ArrowRight className="w-5 h-5 text-lime opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* View All CTA */}
          {featuredArtists.length > 0 && artists.length > 5 && (
            <div className="text-center mt-12">
              <Link 
                to={browseArtistsLink} 
                className="btn-primary inline-flex items-center gap-2"
                data-testid="view-more-artists"
              >
                View All {artists.length} Artists
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <HowItWorksSection />

      {/* CTA Section */}
      <CtaSection isAuthenticated={isAuthenticated} browseArtistsLink={browseArtistsLink} />

      <Footer />
    </div>
  );
}
